import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity,
  Users as UsersIcon,
  Building2,
  CreditCard,
  ArrowRight,
  TriangleAlert,
  ShieldCheck,
  WifiOff,
  RefreshCw,
  Server,
  Database,
  Bot,
  Mail,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import StatCard from "@/components/dashboard/StatCard";
import Button from "@/components/ui/Button";
import { GroupedBars, BarRow } from "@/components/dashboard/Charts";
import LoadError from "@/components/dashboard/LoadError";
import { useApiData } from "@/hooks/useApiData";
import { useRefreshAll } from "@/hooks/useRefreshAll";
import { cn } from "@/lib/cn";
import { getUserStats, listCompanies, listGuardrails, getAiConfig, getHealth, getAnalytics } from "@/api/admin";
import { AGENT_COST, RUNS, USERS, COMPANIES } from "@/data/mock";

const PLAN_LABEL = { free_trial: "Trial", starter: "Starter", growth: "Growth", custom: "Custom" };
const ACTIVE_SUB = ["active", "trialing"];
const ANALYTICS_DAYS = 7;

// Matches the single-letter day labels lib/trends.js produces for the same chart.
const DOW = ["S", "M", "T", "W", "T", "F", "S"];
const dayLabel = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : DOW[d.getDay()];
};
const dayTitle = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};
const COMPANY_CHART_COLORS = ["bg-brand-500", "bg-emerald-500", "bg-amber-500", "bg-sky-500", "bg-violet-500"];
const fmtUsd = (v) => `$${Number(v || 0).toFixed(2)}`;

const ALERT_TONE = {
  rose: "border-rose-200 bg-rose-50/70 text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200",
  amber: "border-amber-200 bg-amber-50/70 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200",
  emerald: "border-emerald-200 bg-emerald-50/70 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200",
  slate: "border-slate-200 bg-slate-50 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300",
};

// Scroll-reveal wrapper for the lower sections.
function Section({ children, delay = 0, className }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function AdminOverview() {
  // The activity panel and the agent-cost card are platform figures, so they come
  // from the platform aggregate. They used to be derived from useRuns({limit:100})
  // — GET /test/runs, which is scoped to the CALLER, so on a real platform they
  // read as zeros next to a non-zero "Platform runs" KPI (and cost 101 requests
  // per load to compute four numbers).
  const { data: analytics, demo: analyticsDemo, error: analyticsError, reload: reloadAnalytics } =
    useApiData(() => getAnalytics(ANALYTICS_DAYS), null);
  // Counts, not rows. This used to call GET /admin/users — every user on the
  // platform, plus a run-count GROUP BY across the entire test_runs table — to
  // compute two integers for two stat cards.
  const { data: userStats, demo: usersDemo, error: usersError, reload: reloadUsers } =
    useApiData(getUserStats, { total: USERS.length, active: USERS.filter((u) => u.status === "active").length });
  const { data: companies, demo: compDemo, error: compError, reload: reloadCompanies } = useApiData(listCompanies, COMPANIES);
  const { data: guardrails, error: guardError, reload: reloadGuards } = useApiData(listGuardrails, []);
  const { data: health, error: healthError, reload: reloadHealth } = useApiData(getHealth, null);
  // The AI provider isn't part of /health, so the card below can't be derived
  // from the API ping — read the real provider config, as the System page does.
  const { data: aiCfg, error: aiError, reload: reloadAi } = useApiData(getAiConfig, null);

  const companyList = Array.isArray(companies) ? companies : [];
  const rules = Array.isArray(guardrails) ? guardrails : [];

  const demo = analyticsDemo || usersDemo || compDemo;
  // useApiData only swaps in the fixture when the backend is unreachable; every
  // other failure (403, 500, bad payload) leaves data null, which the lists
  // above coerce to []. Without surfacing it, a failed load rendered as
  // "Companies 0, Active users 0" — indistinguishable from an empty platform.
  const loadError = usersError || compError || guardError || healthError || aiError || analyticsError;
  // Six reloads fired with no await, no spinner and no confirmation — a press
  // that genuinely refetched everything was indistinguishable from a dead
  // button, since a re-fetch on an otherwise-quiet platform usually returns the
  // same numbers. See useRefreshAll for the same fix already applied elsewhere.
  const { refreshing, refresh: refreshAll } = useRefreshAll(
    [reloadAnalytics, reloadUsers, reloadCompanies, reloadGuards, reloadHealth, reloadAi],
    { label: "Platform overview" },
  );

  // ---- Platform-wide KPIs (real: /admin/companies + /admin/users) ----------
  const totalCompanies = companyList.length;
  const activeCompanies = companyList.filter((c) => c.is_active).length;
  const activeSubs = companyList.filter((c) => ACTIVE_SUB.includes(String(c.status || "").toLowerCase())).length;
  const totalUsers = Number(userStats?.total) || 0;
  const activeUsers = Number(userStats?.active) || 0;

  // ---- Recent-run analytics (real: /admin/analytics, platform-wide) --------
  const totals = (analytics && analytics.totals) || {};
  const series = Array.isArray(analytics && analytics.series) ? analytics.series : [];
  // Offline mode keeps deriving these from the run fixture, like the two charts
  // below; the live path takes them from the platform aggregate.
  // Extension + desktop-agent only — the cloud engine is paused for testers, so
  // folding its historical runs in here would overstate current activity.
  const liveRuns = demo
    ? RUNS.length
    : (totals.runs_by_channel?.extension || 0) + (totals.runs_by_channel?.exe || 0);
  const openBugs = demo ? RUNS.filter((r) => r.bug).length : totals.bugs || 0;
  const passRate = demo
    ? Math.round((RUNS.filter((r) => r.status === "PASSED").length / RUNS.length) * 100)
    : Math.round(totals.pass_rate || 0);

  const agentsAgg = Array.isArray(analytics && analytics.by_agent) ? analytics.by_agent : [];
  // An empty aggregate against a LIVE backend means "no agent spend recorded
  // yet" — showing the fixture there put four invented agents and $62.60 of
  // spend on screen with no demo banner, directly under a real "$0.00" stat.
  // The fixture belongs to the unreachable-backend path only.
  const agents = agentsAgg.length ? agentsAgg : demo ? AGENT_COST : [];
  const maxCost = Math.max(...agents.map((a) => a.cost), 0.0001);

  const spendData = demo ? [1.1, 1.4, 0.9, 1.8, 1.5, 2.2, 1.7] : series.map((s) => s.cost);
  const spend7d = spendData.reduce((s, v) => s + v, 0);
  // Same number as the "Runs (7d)" KPI above, on purpose — this mini-stat used
  // to be summed from a separate per-day series that (via a backend bug)
  // counted the paused cloud flow too, so two tiles on the same page could
  // show two different "Runs (7d)" numbers for what looked like one metric.
  const runs7d = liveRuns;

  // ---- Cost by company, per day (real: /admin/analytics) -------------------
  // Replaces the old runs-per-day bar chart that used to sit here: the run
  // count already has its own KPI above, and what a superadmin actually wants
  // from THIS section is which tenants are costing money, not another runs
  // count. No demo fixture — there is no per-company cost history to fake
  // convincingly, so offline mode just shows the chart's own empty state.
  const dayLabels = series.map((s) => dayLabel(s.date));
  const dayTitles = series.map((s) => dayTitle(s.date));
  const companyCostSeries = (Array.isArray(analytics && analytics.company_cost_series)
    ? analytics.company_cost_series : []
  ).map((c, i) => ({
    key: c.company_id,
    label: c.name,
    color: COMPANY_CHART_COLORS[i % COMPANY_CHART_COLORS.length],
    data: c.data,
  }));

  // ---- Top companies by run volume (real: /admin/companies) ----------------
  const topCompanies = [...companyList].sort((a, b) => (b.runs || 0) - (a.runs || 0)).slice(0, 5);
  // Company names aren't unique — two different tenants can genuinely sign up
  // with the same one (#14) — so track which of the names shown here collide,
  // to surface a real identifier only where it's actually needed.
  const topCompanyNameCounts = topCompanies.reduce((acc, c) => {
    const key = (c.name || "").trim().toLowerCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // ---- System health (real: /health + /admin/ai-config) --------------------
  const apiUp = !!health && health.status === "ok";
  const dbUp = !!health && health.database === "up";
  // GET /health already reports email up/down; this row used to be a hardcoded
  // amber "Not configured" that read the same whether SMTP worked or not — and
  // contradicted the System page one click away, which derives it correctly.
  const smtpUp = !!health && health.email === "up";
  // Field names per GET /admin/ai-config: `key_set` is Anthropic's.
  const aiReady = !!aiCfg && !!(aiCfg.key_set || aiCfg.openai_key_set || aiCfg.ollama_key_set);
  const healthRows = [
    { icon: Server, label: "API server", value: apiUp ? "Operational" : "Down", ok: apiUp },
    { icon: Database, label: "Database", value: health ? (dbUp ? "Operational" : "Down") : "Checking…", ok: dbUp },
    { icon: Bot, label: "AI service", value: aiCfg ? (aiReady ? "Configured" : "No provider key") : "Checking…", ok: aiReady, warn: !!aiCfg && !aiReady },
    { icon: Mail, label: "SMTP", value: health ? (smtpUp ? "Configured" : "Not configured") : "Checking…", ok: smtpUp, warn: !!health && !smtpUp },
  ];
  const overallOk = apiUp && dbUp;

  // ---- Budget alert (real: /admin/guardrails) ------------------------------
  // GET /admin/guardrails evaluates every rule server-side and appends
  // usage_usd/breached/ratio. Recomputing it here from this operator's own last
  // 100 runs reported a hard-stop rule that was actively refusing tenant runs as
  // "all spend is within your guardrails".
  const enabled = rules.filter((g) => g.enabled);
  const over = enabled.filter((g) => g.breached);
  const near = enabled.filter((g) => !g.breached && g.ratio != null && g.ratio >= 0.9);

  let alert;
  if (over.length) {
    alert = { tone: "rose", Icon: TriangleAlert, node: <><span className="font-semibold">{over.length} guardrail{over.length > 1 ? "s" : ""}</span> exceeded their cap.</> };
  } else if (near.length) {
    alert = { tone: "amber", Icon: TriangleAlert, node: <><span className="font-semibold">{near.length} guardrail{near.length > 1 ? "s" : ""}</span> within 10% of the cap.</> };
  } else if (enabled.length) {
    alert = { tone: "emerald", Icon: ShieldCheck, node: <>All spend is within your guardrails.</> };
  } else {
    alert = { tone: "slate", Icon: ShieldCheck, node: <>No guardrails set yet.</> };
  }

  const miniStats = [
    { label: "Runs (7d)", value: runs7d },
    { label: "Spend (7d)", value: `$${spend7d.toFixed(2)}` },
    { label: "Open bugs", value: openBugs },
    { label: "Pass rate", value: `${passRate}%` },
  ];

  return (
    <>
      <PageHeader
        title="Platform Overview"
        subtitle="Company-wide testing, cost, and health at a glance."
        actions={
          <>
            <span
              className={cn(
                "hidden items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold sm:inline-flex",
                overallOk
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                  : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
              )}
            >
              <span className={cn("h-2 w-2 rounded-full", overallOk ? "bg-emerald-500" : "bg-amber-500", "animate-pulse")} />
              {overallOk ? "All systems normal" : "Status degraded"}
            </span>
            <Button variant="outline" size="sm" onClick={refreshAll} disabled={refreshing}>
              <RefreshCw size={16} className={cn(refreshing && "animate-spin")} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </>
        }
      />

      <LoadError message={loadError} onRetry={refreshAll} />

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      {/* Platform KPIs + budget alert — hidden while a load failed, so zeros and
          "all clear" can never stand in for data we never received. */}
      {!loadError && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={Building2} label="Companies" value={totalCompanies} sub={`${activeCompanies} active`} />
            <StatCard icon={UsersIcon} label="Active users" value={activeUsers} accent="sky" sub={`${totalUsers} total`} delay={0.05} />
            <StatCard icon={Activity} label={`Runs (${ANALYTICS_DAYS}d)`} value={liveRuns.toLocaleString()} accent="emerald" sub="extension + desktop agent" delay={0.1} />
            <StatCard icon={CreditCard} label="Active plans" value={activeSubs} accent="amber" sub={`of ${totalCompanies} companies`} delay={0.15} />
          </div>

          <div className={`mt-5 flex items-start gap-3 rounded-2xl border p-4 ${ALERT_TONE[alert.tone]}`}>
            <alert.Icon size={18} className="mt-0.5 shrink-0" />
            <p className="text-sm">
              {alert.node}{" "}
              <Link to="/admin/guardrails" className="font-semibold underline">
                {enabled.length ? "Review guardrails" : "Add a guardrail"}
              </Link>
            </p>
          </div>
        </>
      )}

      {/* Activity + system health */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Section className="lg:col-span-2">
          <div className="card flex h-full flex-col p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Recent activity</h3>
                <p className="text-xs text-slate-400">last {ANALYTICS_DAYS} days · all tenants</p>
              </div>
              <Link to="/admin/analytics" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">
                Analytics <ArrowRight size={15} />
              </Link>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {miniStats.map((s) => (
                <div key={s.label} className="rounded-xl bg-slate-50 p-3 dark:bg-white/5">
                  <p className="text-xs text-slate-400">{s.label}</p>
                  <p className="mt-0.5 font-display text-xl font-bold text-slate-900 dark:text-white">{s.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 border-t border-slate-100 pt-4 dark:border-white/10">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Cost by company, per day</p>
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{fmtUsd(spend7d)} total</p>
              </div>
              <div className="mt-3">
                <GroupedBars
                  labels={dayLabels}
                  titles={dayTitles}
                  series={companyCostSeries}
                  format={fmtUsd}
                  empty="No cost recorded yet in this period."
                />
              </div>
            </div>
          </div>
        </Section>

        <Section delay={0.08}>
          <div className="card flex h-full flex-col p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-white">System health</h3>
              <Link to="/admin/system" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">
                Details <ArrowRight size={15} />
              </Link>
            </div>
            <div className="mt-4 space-y-2.5">
              {healthRows.map((h) => (
                <div key={h.label} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2.5 dark:border-white/5">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300">
                    <h.icon size={16} />
                  </span>
                  <span className="flex-1 text-sm font-medium text-slate-700 dark:text-slate-200">{h.label}</span>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 text-xs font-semibold",
                      h.warn ? "text-amber-500 dark:text-amber-400" : h.ok ? "text-emerald-600 dark:text-emerald-400" : "text-rose-500 dark:text-rose-400"
                    )}
                  >
                    <span className={cn("h-2 w-2 rounded-full", h.warn ? "bg-amber-400" : h.ok ? "bg-emerald-500" : "bg-rose-500")} />
                    {h.value}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[0.6875rem] text-slate-400">
              Source <code className="rounded bg-slate-100 px-1 py-0.5 dark:bg-white/10">GET /health</code> · v{health?.version || "—"}
            </p>
          </div>
        </Section>
      </div>

      {/* Cost by agent + top companies */}
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Section>
          <div className="card h-full p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Cost by agent</h3>
              <Link to="/admin/analytics" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">
                Full analytics <ArrowRight size={15} />
              </Link>
            </div>
            <div className="mt-4 space-y-3">
              {agents.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">No agent spend recorded yet.</p>
              ) : (
                agents.map((a) => (
                  <BarRow key={a.agent} label={a.agent} sub={a.model} value={a.cost} max={maxCost} display={`$${a.cost.toFixed(2)}`} />
                ))
              )}
            </div>
          </div>
        </Section>

        <Section delay={0.08}>
          <div className="card h-full p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Top companies by runs</h3>
              <Link to="/admin/companies" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">
                All companies <ArrowRight size={15} />
              </Link>
            </div>
            <div className="mt-4 space-y-3">
              {loadError ? null : topCompanies.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">No companies yet.</p>
              ) : (
                topCompanies.map((c) => {
                  const isDupName = topCompanyNameCounts[(c.name || "").trim().toLowerCase()] > 1;
                  return (
                    <div key={c.id} className="flex items-center gap-3">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-brand-400/20 to-brand-600/20 text-xs font-bold text-brand-700 dark:text-brand-300">
                        {(c.name || "?").slice(0, 2).toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{c.name}</p>
                          <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[0.625rem] font-semibold text-slate-500 dark:bg-white/10 dark:text-slate-300">
                            {PLAN_LABEL[c.plan_id] || c.plan_id || "—"}
                          </span>
                          {!c.is_active && (
                            <span className="shrink-0 rounded-full bg-rose-100 px-1.5 py-0.5 text-[0.625rem] font-semibold text-rose-600 dark:bg-rose-500/15 dark:text-rose-300">
                              suspended
                            </span>
                          )}
                        </div>
                        {/* Only shown when this row's name actually collides with
                            another one in this same list — every row carrying an
                            ID nobody needs would just be clutter. */}
                        {isDupName && (
                          <p className="truncate text-[0.625rem] text-slate-400">
                            {c.billing_email || `ID ${c.id.slice(0, 8)}`}
                          </p>
                        )}
                        {/* Against THIS company's own plan cap, not the busiest of
                            the other companies shown here (#13) — runs_percent is
                            computed server-side from the company's real plan limit. */}
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                          <div className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-400" style={{ width: `${c.runs_percent ?? 0}%` }} />
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold text-slate-800 dark:text-white">{(c.runs || 0).toLocaleString()}</p>
                        <p className="text-[0.6875rem] text-slate-400">{c.seats || 0} seats</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Section>
      </div>
    </>
  );
}
